import React, { useEffect, useRef } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Stack, xcss } from '@atlaskit/primitives';

import { ScreenEvent } from '@confluence/analytics/entry-points/ScreenEvent';
import { ReactionsProvider } from '@confluence/comment-context/entry-points/ReactionsContext';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary';
import { withFlags } from '@confluence/flags/entry-points/withFlags';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags';
import { usePageContentId } from '@confluence/page-context';
import {
	useCommentsPanelState,
	ViewValues,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { selectInlineCommentCompoundExperience } from '@confluence/experience-tracker/entry-points/selectInlineCommentCompoundExperience';
import { fg } from '@confluence/feature-gating';

import { OpenView } from './views/OpenView';
import { UnreadView } from './views/UnreadView';
import { DeletedContentView } from './views/DeletedContentView';
import { ResolvedView } from './views/ResolvedView';

type CommentsPanelProps = {
	currentView: ViewValues;
	// Disable loading UI for VR tests
	disabledLoading?: boolean;
};

const commentsPanelViews: Record<ViewValues, (props: any) => JSX.Element> = {
	[ViewValues.OPEN]: OpenView,
	[ViewValues.UNREAD]: UnreadView,
	[ViewValues.DELETED_CONTENT]: DeletedContentView,
	[ViewValues.RESOLVED]: ResolvedView,
};

const commentPanelContainerStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	height: '100%',
});

const CommentsPanelComponent = withFlags(
	({ currentView, flags, disabledLoading = false }: CommentsPanelProps & WithFlagsProps) => {
		const initialRender = React.useRef(true);

		if (initialRender.current === true) {
			// On initial render -- dismiss any active inline comment experiences
			// While there is abort firing from the Inline Comment being dismounted -- it's possible for that code to
			// not have been triggered by the time the inline comment sidebar UI is being shown
			// ie. selection from comment url, and the user clicks the comment button to open the side panel
			// before the page has loaded in comments.
			selectInlineCommentCompoundExperience.abort({
				abortReason: 'Comment Side Panel opened',
			});
			initialRender.current = false;
		}

		const [contentId] = usePageContentId();
		const pageContainsBodiedMacrosRef = useRef(false);

		const { currentView: userSelectedView } = useCommentsPanelState();

		const viewToShow = userSelectedView || currentView;

		// We need to check that there are page properties macros or excerpt macros on the page
		// so we can hide the delete option which causes weird issues in the document
		useEffect(() => {
			const bodiedMacroElem = document.querySelector('.ak-renderer-extension');
			pageContainsBodiedMacrosRef.current = Boolean(bodiedMacroElem);
		}, []);

		const CommentView = commentsPanelViews[viewToShow];

		return (
			<>
				<Stack xcss={commentPanelContainerStyles}>
					<ReactionsProvider contentId={contentId ?? ''}>
						<CommentView
							flags={flags}
							showDeleteOption={
								fg('confluence_comment_nested_renderer_delete') ||
								!pageContainsBodiedMacrosRef.current
							}
							disabledLoading={disabledLoading}
						/>
					</ReactionsProvider>
				</Stack>
				<ScreenEvent
					name="commentsPanelScreen"
					id="commentsPanelScreen"
					attributes={{ currentView }}
				/>
			</>
		);
	},
);

export const CommentsPanel = withErrorBoundary({
	attribution: Attribution.COMMENTS,
})(CommentsPanelComponent);
